import {createHooks} from "@wordpress/hooks";

/**
 * Actions - Shared event bus for controllers, gateways, and cart
 *
 * Provides a centralized event system that allows different components
 * to communicate through actions and filters. Implemented as a singleton
 * to ensure all components share the same event bus instance.
 *
 * @example
 * import Actions from './core/Actions';
 *
 * const actions = new Actions(); // Gets singleton instance
 *
 * // Listen to events
 * actions.addAction('before_payment_sheet_open', (gateway) => {
 *   console.log('Payment sheet opening');
 * });
 *
 * // Trigger events
 * actions.doAction('before_payment_sheet_open', this);
 *
 * // Apply filters
 * const data = actions.applyFilters('cartData', {total: 100}, this);
 */
class Actions {
    constructor() {
        // Singleton pattern - return existing instance if already created
        if (Actions.instance) {
            return Actions.instance;
        }

        this.hooks = createHooks();
        Actions.instance = this;
    }

    /**
     * Add an action callback
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     * @param {number} priority - Priority (default 10)
     */
    addAction(event, callback, priority = 10) {
        this.hooks.addAction(event, 'wcPPCP', callback, priority);
    }

    /**
     * Trigger an action
     * @param {string} event - Event name
     * @param {...*} args - Arguments to pass to callbacks
     */
    doAction(event, ...args) {
        this.hooks.doAction(event, ...args);
    }

    /**
     * Add a filter callback
     * @param {string} event - Filter name
     * @param {Function} callback - Callback function
     * @param {number} priority - Priority (default 10)
     */
    addFilter(event, callback, priority = 10) {
        this.hooks.addFilter(event, 'wcPPCP', callback, priority);
    }

    /**
     * Apply filters
     * @param {string} event - Filter name
     * @param {...*} args - Arguments to pass to callbacks (first arg is the value to filter)
     * @returns {*} Filtered value
     */
    applyFilters(event, ...args) {
        return this.hooks.applyFilters(event, ...args);
    }

    /**
     * Run async filters sequentially, awaiting each callback and passing the
     * updated first argument to the next — mirrors WordPress asyncRunner with
     * returnFirstArg=true. Falls back to native when available.
     */
    async applyFiltersAsync(event, ...args) {
        if (typeof this.hooks.applyFiltersAsync === 'function') {
            return this.hooks.applyFiltersAsync(event, ...args);
        }
        const handlers = this.hooks.filters[event]?.handlers || [];
        let result = args[0];
        for (const {callback} of handlers) {
            result = await callback.apply(null, [result, ...args.slice(1)]);
        }
        return result;
    }

    /**
     * Run async actions sequentially, awaiting each callback — mirrors
     * WordPress asyncRunner with returnFirstArg=false. Falls back to native
     * when available.
     */
    async doActionAsync(event, ...args) {
        if (typeof this.hooks.doActionAsync === 'function') {
            return this.hooks.doActionAsync(event, ...args);
        }
        const handlers = this.hooks.actions[event]?.handlers || [];
        for (const {callback} of handlers) {
            await callback.apply(null, args);
        }
    }
}

export default Actions;